import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, StyleSheet, FlatList, Alert, TouchableOpacity } from 'react-native';
import { Text, Card, ActivityIndicator, Divider, Button, TextInput } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import api from '../../services/api';
import BarcodeScannerModal from '../../components/BarcodeScannerModal';

const DetalleConteoScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { conteo: conteoParam } = route.params;

  const [loading, setLoading] = useState(true);
  const [conteo, setConteo] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [filtro, setFiltro] = useState('todos');
  const [showScanner, setShowScanner] = useState(false);
  const listRef = useRef(null);

  const loadConteo = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getConteo(conteoParam.id);
      setConteo(data);
    } catch (error) {
      Alert.alert('Error', 'No se pudo cargar el conteo');
    } finally {
      setLoading(false);
    }
  }, [conteoParam.id]);

  useEffect(() => { loadConteo(); }, [loadConteo]);

  const handleRegistrar = async (detalle) => {
    const valor = parseFloat(editValue);
    if (isNaN(valor) || valor < 0) {
      Alert.alert('Error', 'Ingrese una cantidad valida');
      return;
    }

    try {
      await api.registrarConteoDetalle(conteo.id, detalle.id, { stock_fisico: valor });
      setEditingId(null);
      setEditValue('');
      loadConteo();
    } catch (error) {
      Alert.alert('Error', error.message);
    }
  };

  const handleCompletar = () => {
    const sinContar = (conteo.detalles || []).filter(d => d.stock_fisico === null).length;
    if (sinContar > 0) {
      Alert.alert('Conteo Incompleto', `Hay ${sinContar} productos sin contar. ¿Completar de todas formas?`, [
        { text: 'Cancelar', style: 'cancel' },
        { text: 'Completar', onPress: () => showCompletarOptions() },
      ]);
    } else {
      showCompletarOptions();
    }
  };

  const showCompletarOptions = () => {
    const diffs = (conteo.detalles || []).filter(d => d.stock_fisico !== null && parseFloat(d.diferencia) !== 0).length;
    Alert.alert(
      'Completar Conteo',
      `Se encontraron ${diffs} diferencias.\n¿Desea ajustar el stock del sistema al conteo fisico?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        { text: 'Solo Completar', onPress: () => doCompletar(false) },
        { text: 'Completar y Ajustar Stock', style: 'destructive', onPress: () => doCompletar(true) },
      ]
    );
  };

  const doCompletar = async (ajustar) => {
    try {
      setProcessing(true);
      const result = await api.completarConteo(conteo.id, { ajustar_stock: ajustar });
      Alert.alert('Conteo Completado', result.message, [
        { text: 'OK', onPress: () => navigation.goBack() }
      ]);
    } catch (error) {
      Alert.alert('Error', error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleScanned = (codigo) => {
    setShowScanner(false);
    if (!conteo?.detalles) return;

    const codigoNorm = String(codigo).trim();
    const match = conteo.detalles.find(d =>
      d.producto_codigo && String(d.producto_codigo).trim() === codigoNorm
    );

    if (!match) {
      Alert.alert('No encontrado', `El código ${codigoNorm} no está en este conteo.`);
      return;
    }

    // Asegurar que el item sea visible: si el filtro actual lo oculta, volver a "todos".
    const visibleEnFiltroActual = getDetallesFiltrados().some(d => d.id === match.id);
    if (!visibleEnFiltroActual) setFiltro('todos');

    setEditingId(match.id);
    setEditValue(match.stock_fisico !== null ? String(parseFloat(match.stock_fisico)) : '');

    // Scroll al item escaneado (con un retraso para esperar al re-render por el filtro)
    setTimeout(() => {
      const lista = filtro === 'todos' || visibleEnFiltroActual ? getDetallesFiltrados() : conteo.detalles;
      const idx = lista.findIndex(d => d.id === match.id);
      if (idx >= 0 && listRef.current) {
        try {
          listRef.current.scrollToIndex({ index: idx, animated: true, viewPosition: 0.3 });
        } catch (e) {
          // scrollToIndex puede fallar si el item aún no se midió; ignorar
        }
      }
    }, 150);
  };

  const getDetallesFiltrados = () => {
    if (!conteo?.detalles) return [];
    switch (filtro) {
      case 'pendientes': return conteo.detalles.filter(d => d.stock_fisico === null);
      case 'diferencias': return conteo.detalles.filter(d => d.stock_fisico !== null && parseFloat(d.diferencia) !== 0);
      case 'ok': return conteo.detalles.filter(d => d.stock_fisico !== null && parseFloat(d.diferencia) === 0);
      default: return conteo.detalles;
    }
  };

  const renderItem = ({ item }) => {
    const contado = item.stock_fisico !== null;
    const diff = contado ? parseFloat(item.diferencia) : null;
    const isEditing = editingId === item.id;

    return (
      <Card style={[styles.itemCard, diff !== null && diff !== 0 && { borderLeftWidth: 3, borderLeftColor: diff > 0 ? '#28A745' : '#DC3545' }]}>
        <Card.Content>
          <View style={styles.itemRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.itemName}>{item.producto_nombre || 'Producto'}</Text>
              <View style={{ flexDirection: 'row', marginTop: 2 }}>
                {item.marca && <Text style={styles.itemMarca}>{item.marca}</Text>}
                {item.producto_codigo && <Text style={styles.itemCodigo}>{item.producto_codigo}</Text>}
              </View>
            </View>
            <View style={styles.stockInfo}>
              <Text style={styles.stockLabel}>Sistema</Text>
              <Text style={styles.stockNum}>{parseFloat(item.stock_sistema)}</Text>
            </View>
            <View style={styles.stockInfo}>
              <Text style={styles.stockLabel}>Fisico</Text>
              {conteo.estado === 'en_progreso' ? (
                isEditing ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <TextInput
                      value={editValue}
                      onChangeText={setEditValue}
                      keyboardType="numeric"
                      style={styles.editInput}
                      autoFocus
                      dense
                    />
                    <TouchableOpacity onPress={() => handleRegistrar(item)} style={styles.saveBtn}>
                      <Ionicons name="checkmark" size={18} color="#28A745" />
                    </TouchableOpacity>
                  </View>
                ) : (
                  <TouchableOpacity onPress={() => { setEditingId(item.id); setEditValue(contado ? String(parseFloat(item.stock_fisico)) : ''); }}>
                    <Text style={[styles.stockNum, !contado && { color: '#999' }]}>
                      {contado ? parseFloat(item.stock_fisico) : '---'}
                    </Text>
                  </TouchableOpacity>
                )
              ) : (
                <Text style={styles.stockNum}>{contado ? parseFloat(item.stock_fisico) : '---'}</Text>
              )}
            </View>
            {contado && diff !== 0 && (
              <View style={styles.stockInfo}>
                <Text style={styles.stockLabel}>Diff</Text>
                <Text style={[styles.diffNum, { color: diff > 0 ? '#28A745' : '#DC3545' }]}>
                  {diff > 0 ? '+' : ''}{diff}
                </Text>
              </View>
            )}
          </View>
        </Card.Content>
      </Card>
    );
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007BFF" />
        <Text style={{ marginTop: 10, color: '#666' }}>Cargando conteo...</Text>
      </View>
    );
  }

  if (!conteo) return null;

  const detalles = getDetallesFiltrados();
  const totalContados = (conteo.detalles || []).filter(d => d.stock_fisico !== null).length;
  const totalProductos = (conteo.detalles || []).length;
  const progreso = totalProductos > 0 ? Math.round((totalContados / totalProductos) * 100) : 0;

  return (
    <View style={styles.container}>
      {/* Resumen */}
      <Card style={styles.headerCard}>
        <Card.Content>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <View>
              <Text style={styles.title}>Conteo #{conteo.id}</Text>
              <Text style={styles.subtitle}>{new Date(conteo.fecha).toLocaleDateString('es-HN')} - {conteo.usuario_nombre}</Text>
            </View>
            <Text style={styles.progressText}>{totalContados}/{totalProductos} ({progreso}%)</Text>
          </View>
          <View style={styles.progressBar}>
            <View style={[styles.progressFill, { width: `${progreso}%`, backgroundColor: progreso === 100 ? '#28A745' : '#007BFF' }]} />
          </View>
        </Card.Content>
      </Card>

      {/* Filtros */}
      <View style={styles.filtros}>
        {[
          { key: 'todos', label: `Todos (${totalProductos})` },
          { key: 'pendientes', label: `Pendientes (${totalProductos - totalContados})` },
          { key: 'diferencias', label: `Diferencias` },
        ].map(f => (
          <TouchableOpacity key={f.key} style={[styles.filtroChip, filtro === f.key && styles.filtroActivo]} onPress={() => setFiltro(f.key)}>
            <Text style={[styles.filtroText, filtro === f.key && { color: '#fff' }]}>{f.label}</Text>
          </TouchableOpacity>
        ))}
        {conteo.estado === 'en_progreso' && (
          <TouchableOpacity style={styles.scanChip} onPress={() => setShowScanner(true)}>
            <Ionicons name="barcode" size={16} color="#fff" />
            <Text style={styles.scanChipText}>Escanear</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Lista */}
      <FlatList
        ref={listRef}
        data={detalles}
        keyExtractor={item => item.id.toString()}
        renderItem={renderItem}
        contentContainerStyle={{ padding: 8, paddingBottom: 80 }}
        ItemSeparatorComponent={() => <View style={{ height: 6 }} />}
        onScrollToIndexFailed={({ index }) => {
          setTimeout(() => {
            try { listRef.current?.scrollToIndex({ index, animated: true, viewPosition: 0.3 }); } catch (e) {}
          }, 250);
        }}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons name="checkmark-done-circle-outline" size={48} color="#ccc" />
            <Text style={{ color: '#888', marginTop: 8 }}>Sin productos en este filtro</Text>
          </View>
        }
      />

      <BarcodeScannerModal
        visible={showScanner}
        onClose={() => setShowScanner(false)}
        onScanned={handleScanned}
      />

      {/* Botón completar */}
      {conteo.estado === 'en_progreso' && (
        <View style={styles.bottomBar}>
          <Button
            mode="contained"
            icon="check-all"
            onPress={handleCompletar}
            style={{ backgroundColor: '#28A745', flex: 1 }}
            loading={processing}
            disabled={processing}
          >
            Completar Conteo
          </Button>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  headerCard: { margin: 8, elevation: 2 },
  title: { fontSize: 18, fontWeight: 'bold', color: '#333' },
  subtitle: { fontSize: 13, color: '#888', marginTop: 2 },
  progressText: { fontSize: 16, fontWeight: 'bold', color: '#007BFF' },
  progressBar: { height: 8, backgroundColor: '#E9ECEF', borderRadius: 4, marginTop: 10, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 4 },
  filtros: { flexDirection: 'row', paddingHorizontal: 8, marginBottom: 4 },
  filtroChip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: '#E9ECEF', marginRight: 6 },
  filtroActivo: { backgroundColor: '#007BFF' },
  filtroText: { fontSize: 12, color: '#666' },
  scanChip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 16, backgroundColor: '#6F42C1', marginLeft: 'auto' },
  scanChipText: { fontSize: 12, color: '#fff', fontWeight: '600', marginLeft: 4 },
  itemCard: { elevation: 1 },
  itemRow: { flexDirection: 'row', alignItems: 'center' },
  itemName: { fontSize: 14, fontWeight: '600', color: '#333' },
  itemMarca: { fontSize: 11, color: '#17A2B8', marginRight: 6 },
  itemCodigo: { fontSize: 11, color: '#999' },
  stockInfo: { alignItems: 'center', marginLeft: 12, minWidth: 45 },
  stockLabel: { fontSize: 9, color: '#888', textTransform: 'uppercase' },
  stockNum: { fontSize: 16, fontWeight: 'bold', color: '#333' },
  diffNum: { fontSize: 14, fontWeight: 'bold' },
  editInput: { width: 50, height: 32, fontSize: 14, backgroundColor: '#fff', textAlign: 'center', padding: 0 },
  saveBtn: { padding: 4, marginLeft: 4 },
  emptyContainer: { justifyContent: 'center', alignItems: 'center', paddingVertical: 40 },
  bottomBar: { padding: 12, backgroundColor: '#fff', elevation: 4, flexDirection: 'row' },
});

export default DetalleConteoScreen;
